/**
 * ProgressBar — shows mastery progress for a curriculum node.
 *
 * Three segments: mastered (filled), practicing (half-filled), introduced (empty fill).
 * Uses semantic tier colors from the design tokens.
 */

import { type ReactNode } from "react";

export interface ProgressBarProps {
  total: number;
  mastered: number;
  practicing: number;
  introduced: number;
  /** Show numeric label like "5/7 mastered". */
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
}

function formatBreakdown(
  mastered: number,
  practicing: number,
  introduced: number,
  total: number,
): string {
  // Mastered is the headline metric, so it always appears with its
  // out-of-total fraction. Practicing/introduced are secondary and
  // only render when non-zero to keep the line tight.
  const parts: string[] = [`${mastered} / ${total} mastered`];
  if (practicing > 0) parts.push(`${practicing} practicing`);
  if (introduced > 0) parts.push(`${introduced} introduced`);
  return parts.join(" · ");
}

export function ProgressBar({
  total,
  mastered,
  practicing,
  introduced,
  showLabel = false,
  size = "md",
}: ProgressBarProps): ReactNode {
  const heightClass = size === "sm" ? "h-1" : size === "lg" ? "h-3" : "h-2";
  const masteredPct = total > 0 ? (mastered / total) * 100 : 0;
  const practicingPct = total > 0 ? (practicing / total) * 100 : 0;
  const introducedPct = total > 0 ? (introduced / total) * 100 : 0;

  return (
    <div>
      <div
        className={`relative w-full ${heightClass} bg-paperDeep overflow-hidden rounded-sm`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={mastered}
        aria-label={`${mastered} of ${total} items mastered`}
      >
        <div
          className="absolute left-0 top-0 h-full bg-success motion-safe:transition-all motion-safe:duration-300"
          style={{ width: `${masteredPct}%` }}
        />
        <div
          className="absolute top-0 h-full bg-info motion-safe:transition-all motion-safe:duration-300"
          style={{ left: `${masteredPct}%`, width: `${practicingPct}%` }}
        />
        <div
          className="absolute top-0 h-full bg-warning/40 motion-safe:transition-all motion-safe:duration-300"
          style={{ left: `${masteredPct + practicingPct}%`, width: `${introducedPct}%` }}
        />
      </div>
      {showLabel && (
        <div className="mt-1 text-xs font-mono text-inkFaded tabular-nums">
          {formatBreakdown(mastered, practicing, introduced, total)}
        </div>
      )}
    </div>
  );
}
